<template>
  <div class="live-reports-root flex-1 overflow-auto p-4">
    <div class="row leads-schedule-row">
      <div class="leads-panel flex-1">
      <div class="priority-leads-card">
      <metric-card :header="''" :is-live="false">
        <template #header-control>
          <div class="schedule-header-row">
            <woot-tabs
              :index="activeLeadsTabIndex"
              class="schedule-tabs"
              @change="onLeadsTabChange"
            >
              <woot-tabs-item name="Tasks" :show-badge="false" />
              <woot-tabs-item name="Priority Leads" :show-badge="false" />
            </woot-tabs>
            <woot-button
              v-if="showLeadsReload"
              v-tooltip.top="$t('OVERVIEW_REPORTS.PRIORITY_LEADS.RELOAD')"
              icon="arrow-clockwise"
              size="small"
              variant="smooth"
              color-scheme="secondary"
              class="priority-leads-reload-btn"
              :is-loading="leadsReloadLoading"
              :aria-label="$t('OVERVIEW_REPORTS.PRIORITY_LEADS.RELOAD')"
              @click="reloadActiveLeadsTab"
            />
          </div>
        </template>
        <template #control>
          <div class="priority-leads-toolbar relative">
            <div
              v-if="isTasksTab && isAdmin"
              class="priority-leads-filter-slot priority-leads-filter-slot--dropdown priority-leads-filter-slot--dock-end"
            >
              <woot-button
                variant="smooth"
                size="small"
                color-scheme="primary"
                class="board-button priority-leads-filter-btn"
                @click="toggleAgentModal"
              >
                <span
                  class="board-button__text priority-leads-filter-text w-full truncate text-left rtl:text-right"
                  v-tooltip.right="taskUserFilterTooltip"
                >
                  {{ taskUserButtonText }}
                </span>
              </woot-button>
              <transition name="fade">
                <div
                  v-if="showAgentModal"
                  class="priority-leads-dropdown-anchor priority-leads-dropdown-anchor--dock-end"
                >
                  <state-selection-modal-lead
                    attach-below
                    align-menu-right
                    :heading="$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_AGENT')"
                    :search-placeholder="$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SEARCH_AGENT')"
                    :states="taskUserFilterOptions"
                    :selected-state="selectedTaskUser"
                    @close="toggleAgentModal"
                    @select="handleTaskUserSelection"
                  />
                </div>
              </transition>
            </div>
            <div
              v-else-if="isPriorityLeadsTab"
              class="priority-leads-filter-slot priority-leads-filter-slot--dropdown priority-leads-filter-slot--dock-end"
            >
              <woot-button
                variant="smooth"
                size="small"
                color-scheme="primary"
                class="board-button priority-leads-filter-btn"
                @click="toggleSegmentModal"
              >
                <span
                  class="board-button__text priority-leads-filter-text w-full truncate text-left rtl:text-right"
                  v-tooltip.right="segmentFilterTooltip"
                >
                  {{ priorityLeadsSegmentButtonText }}
                </span>
              </woot-button>
              <transition name="fade">
                <div
                  v-if="showSegmentModal"
                  class="priority-leads-dropdown-anchor priority-leads-dropdown-anchor--dock-end"
                >
                  <state-selection-modal-lead
                    attach-below
                    align-menu-right
                    :heading="$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_SEGMENT')"
                    :search-placeholder="$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SEARCH_SEGMENT')"
                    :states="hotSegments"
                    :selected-state="selectedHotSegment"
                    @close="toggleSegmentModal"
                    @select="handleSegmentSelection"
                  />
                </div>
              </transition>
            </div>

            <woot-button
              v-if="showLeadsFullscreen"
              v-tooltip.top="leadsFullscreenTooltip"
              icon="fullscreen"
              variant="smooth"
              size="small"
              color-scheme="secondary"
              class-names="priority-leads-segment-open-btn"
              view-box="-4 -4 24 24"
              @click="openActiveLeadsFullscreen"
            />
          </div>
        </template>
        <daily-work-tasks
          v-if="isTasksTab"
          :key="`daily-work-${$route.params.accountId}`"
          ref="dailyWorkTasks"
          embedded
          :agents="agents"
          :is-admin="isAdmin"
          :selected-user-id="selectedTaskUserId"
          @open-contact="openContactInfoPanel"
          @loading="isLoadingDailyTasks = $event"
        />
        <div v-show="isPriorityLeadsTab" class="table-wrapper">
          <div :class="{ 'blurred-content': isLoadingHotLeads }">
            <contact-table
              :height="priorityLeadsTableHeight"
              :contacts="hotLeadsItems"
              :meta="hotLeadsMeta"
              :on-click-contact="openContactInfoPanel"
              :is-label-selected="!!selectedHotSegmentId"
              :empty-filter-message="
                $t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_SEGMENT')
              "
              :page-index="contactPageIndex"
              :is-loading="false"
              :is-reminders="false"
              @page-change="onContactPageChange"
            />
          </div>

          <div v-if="isLoadingHotLeads" class="loading-overlay">
            <woot-spinner />
          </div>
        </div>
      </metric-card>
      </div>
    </div>
      <div class="schedule-panel row flex-1">
        <div class="schedule-metrics-row w-full flex">
      <div class="w-1/2">
        <metric-card
          :header="this.$t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.HEADER')"
          :is-loading="openConversationsMetricCardLoading"
          :loading-message="
            $t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.LOADING_MESSAGE')
          "
        >
          <div
            v-for="(metric, name, index) in conversationMetrics"
            :key="index"
            class="metric-content column"
          >
            <h3 class="heading !text-[13px]">
              {{ name }}
            </h3>
            <p
              class="metric"
              v-tooltip.top="metric.tooltip || ''"
            >
              {{ metric.display }}
            </p>
          </div>
        </metric-card>
      </div>
      <div class="w-1/2">
        <metric-card :header="this.$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
          <div
            v-for="(metric, name, index) in agentStatusMetrics"
            :key="index"
            class="metric-content column"
          >
            <h3 class="heading !text-[13px]">
              {{ name }}
            </h3>
            <p
              class="metric"
              v-tooltip.top="metric.tooltip || ''"
            >
              {{ metric.display }}
            </p>
          </div>
        </metric-card>
      </div>
    </div>
    <div class="flex-1 reminders-section-wrapper">
      <metric-card :header="''" :is-live="false">
        <template #header-control>
          <div class="schedule-header-row">
            <woot-tabs
              :index="activeScheduleTabIndex"
              class="schedule-tabs"
              @change="onScheduleTabChange"
            >
              <woot-tabs-item name="Meetings" :count="meetingsTabCount" />
              <woot-tabs-item name="Reminders" :count="remindersTabCount" />
            </woot-tabs>
            <woot-button
              icon="arrow-clockwise"
              size="small"
              variant="smooth"
              color-scheme="secondary"
              class="reload-schedule-btn"
              :is-loading="isLoadingMeetings || isLoadingReminders"
              @click="reloadScheduleData"
              v-tooltip.top="'Reload'"
            />
          </div>
        </template>
        <template #control>
          <div class="flex items-center gap-2">
            <div class="multiselect-wrap--small z-[3]">
              <multiselect
                v-model="selectedDateRange"
                :options="dateRangeOptions"
                :searchable="false"
                :show-labels="false"
                :allow-empty="false"
                track-by="id"
                label="name"
                class="no-margin"
                @select="onDateRangeChange"
                @input="onDateRangeChange"
              />
            </div>
          </div>
        </template>
        <div class="schedule-content-wrapper">
          <div v-if="showCustomDatePicker" class="custom-date-picker-wrapper">
            <div class="custom-date-picker-row">
              <div class="date-field">
                <label class="date-label">From</label>
                <input 
                  v-model="customDateFrom" 
                  type="date" 
                  class="date-input"
                  @change="onFromDateChange"
                />
              </div>
              <div class="date-field">
                <label class="date-label">To</label>
                <input 
                  v-model="customDateTo" 
                  type="date" 
                  class="date-input"
                  :min="customDateFrom"
                />
              </div>
              <woot-button
                size="small"
                color-scheme="primary"
                class="apply-btn"
                @click="applyCustomDateRange"
              >
                Apply
              </woot-button>
            </div>
          </div>
          <contact-table
            v-if="activeScheduleTab === 'reminders'"
            :height="reminderTableHeight"
            :contacts="displayReminderItems"
            :meta="remindersMeta"
            :page-index="remindersPageIndex"
            :is-loading="isLoadingReminders"
            :is-reminders="true"
            :is-meetings="false"
            :show-schedule-toolbar="true"
            :show-completed-in-list="showCompletedReminders"
            :completed-today-count="remindersMeta.completedTodayCount || 0"
            :selected-date-range="selectedDateRange"
            :on-click-contact="openScheduleContactPanel"
            @page-change="onRemindersPageChange"
            @update-reminder="onUpdateReminder"
            @complete-reminder="onCompleteReminder"
          >
            <template #schedule-toolbar>
              <woot-button
                v-if="!showCompletedReminders"
                variant="smooth"
                size="small"
                color-scheme="secondary"
                class="w-full text-center"
                :is-loading="isLoadingReminders"
                @click="loadCompletedReminders"
              >
                {{ loadCompletedRemindersButtonLabel }}
              </woot-button>
              <woot-button
                v-else
                variant="smooth"
                size="small"
                color-scheme="secondary"
                class="w-full text-center"
                :is-loading="isLoadingReminders"
                @click="hideCompletedReminders"
              >
                {{ $t('OVERVIEW_REPORTS.PRIORITY_LEADS.HIDE_COMPLETED_REMINDERS') }}
              </woot-button>
            </template>
          </contact-table>
          <contact-table
            v-else
            :height="meetingsTableHeight"
            :contacts="displayMeetingItems"
            :meta="meetingsMeta"
            :page-index="meetingsPageIndex"
            :is-loading="isLoadingMeetings"
            :is-reminders="false"
            :is-meetings="true"
            :show-schedule-toolbar="true"
            :show-completed-in-list="showCompletedMeetings"
            :completed-today-count="meetingsMeta.completedTodayCount || 0"
            :selected-date-range="selectedDateRange"
            :on-click-contact="openScheduleContactPanel"
            @page-change="onMeetingsPageChange"
            @reschedule-meeting="onRescheduleMeeting"
            @cancel-meeting="onCancelMeeting"
            @meeting-status="onMeetingStatus"
          >
            <template #schedule-toolbar>
              <woot-button
                v-if="!showCompletedMeetings"
                variant="smooth"
                size="small"
                color-scheme="secondary"
                class="w-full text-center"
                :is-loading="isLoadingMeetings"
                @click="loadCompletedMeetings"
              >
                {{ loadCompletedMeetingsButtonLabel }}
              </woot-button>
              <woot-button
                v-else
                variant="smooth"
                size="small"
                color-scheme="secondary"
                class="w-full text-center"
                :is-loading="isLoadingMeetings"
                @click="hideCompletedMeetings"
              >
                {{ $t('OVERVIEW_REPORTS.PRIORITY_LEADS.HIDE_COMPLETED_MEETINGS') }}
              </woot-button>
            </template>
          </contact-table>
          <meeting-demo-status-bar
            v-if="showMeetingDemoStatusBar"
            :done-count="meetingDemoStatus.doneCount"
            :not-done-count="meetingDemoStatus.notDoneCount"
            :very-good-count="meetingDemoStatus.veryGoodCount"
          />
        </div>
      </metric-card>
    </div>
    </div>
    </div>
    <div class="row">
      <metric-card :header="this.$t('OVERVIEW_REPORTS.TRAFFIC_HEATMAP.SECTION_TITLE')">
        <template #header-control>
          <div class="multiselect-wrap--small z-[3] min-w-[220px]">
            <multiselect
              :value="currentTrafficHeatmapOption"
              :options="trafficHeatmapOptions"
              :searchable="false"
              :show-labels="false"
              :allow-empty="false"
              open-direction="bottom"
              track-by="id"
              label="name"
              class="no-margin"
              @select="onTrafficHeatmapTypeSelect"
            />
          </div>
        </template>
        <template #control>
          <woot-button
            icon="arrow-download"
            size="small"
            variant="smooth"
            color-scheme="secondary"
            @click="downloadTrafficHeatmapData"
          >
            Download Report
          </woot-button>
        </template>
        <report-heatmap
          :heat-data="trafficHeatmapHeatData"
          :is-loading="trafficHeatmapLoading"
          :entity-type="trafficHeatmapEntityType"
        />
      </metric-card>
    </div>
    <div class="admin-call-stack">
      <div class="admin-call-stack__section">
        <call-history-section />
      </div>
      <div v-if="!isAgentRole" class="admin-call-stack__section">
        <metric-card
          :header="this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER')"
        >
          <template #control>
            <div class="multiselect-wrap--small agent-conversations-date-filter z-[3]">
              <multiselect
                :value="selectedAgentDateRange"
                :options="agentDateRangeOptions"
                :searchable="false"
                :show-labels="false"
                :allow-empty="false"
                track-by="id"
                label="name"
                class="no-margin"
                @select="onAgentDateRangeSelect"
              />
            </div>
          </template>
          <agent-table
            :agents="agents"
            :agent-metrics="agentConversationMetric"
            :page-index="pageIndex"
            :is-loading="uiFlags.isFetchingAgentConversationMetric"
            :show-custom-date-picker="showAgentCustomDatePicker"
            @page-change="onPageNumberChange"
            @agent-click="openAgentResponseTimeDetail"
          >
            <template v-if="showAgentCustomDatePicker" #custom-date-picker>
              <div class="custom-date-picker-wrapper">
                <div class="custom-date-picker-row">
                  <div class="date-field">
                    <label class="date-label">From</label>
                    <input
                      v-model="agentCustomDateFrom"
                      type="date"
                      class="date-input"
                      :max="agentCustomDateTo ? (agentCustomDateTo < agentTodayYmd ? agentCustomDateTo : agentTodayYmd) : agentTodayYmd"
                      @change="onAgentFromDateChange"
                    />
                  </div>
                  <div class="date-field">
                    <label class="date-label">To</label>
                    <input
                      v-model="agentCustomDateTo"
                      type="date"
                      class="date-input"
                      :min="agentCustomDateFrom || undefined"
                      :max="agentTodayYmd"
                      @change="onAgentToDateChange"
                    />
                  </div>
                  <woot-button
                    size="small"
                    color-scheme="primary"
                    class="apply-btn"
                    @click="applyAgentCustomDateRange"
                  >
                    Apply
                  </woot-button>
                </div>
              </div>
            </template>
          </agent-table>
        </metric-card>
      </div>
    </div>

    <div
      v-show="showContactViewPane"
      class="modal-overlay-v1"
      @click="closeContactInfoPanel"
    ></div>
    <contact-info-panel
      v-if="showContactViewPane"
      :contact="Contact"
      :on-close="closeContactInfoPanel"
      :is-modal-view="true"
      @contact-deleted="onContactDeleted"
    />

    <div
      v-show="showAgentResponseTimeDetail"
      class="modal-overlay-v1"
      @click="closeAgentResponseTimeDetail"
    ></div>
    <agent-response-time-detail-modal
      v-if="showAgentResponseTimeDetail"
      :agent="selectedArtAgent"
      :since="agentDetailSince"
      :until="agentDetailUntil"
      @close="closeAgentResponseTimeDetail"
    />

    <SetRemainderFollowUpModal
      v-if="showReminderFollowUpModal && reminderActionContact"
      :primary-contact="reminderActionContact"
      :show="showReminderFollowUpModal"
      @close="closeReminderFollowUpModal"
      @success-message="onReminderFollowUpSuccess"
    />
    <MeetingStatusModal
      v-if="showMeetingStatusModal && meetingStatusContact"
      :contact="meetingStatusContact"
      :meeting-id="meetingStatusMeetingId"
      :show="showMeetingStatusModal"
      @close="closeMeetingStatusModal"
      @success="onMeetingStatusSuccess"
    />
    <woot-confirm-modal
      ref="cancelMeetingDialog"
      :title="$t('EDIT_CONTACT.CANCEL_MEETING_CONFIRM_TITLE')"
      :description="$t('EDIT_CONTACT.CANCEL_MEETING_CONFIRM_DESCRIPTION')"
      :confirm-label="$t('EDIT_CONTACT.CANCEL_MEETING_CONFIRM')"
      :cancel-label="$t('EDIT_CONTACT.CANCEL_MEETING_KEEP')"
    />
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import AgentTable from './components/overview/AgentTable';
import AgentResponseTimeDetailModal from './components/overview/AgentResponseTimeDetailModal.vue';
import ContactTable from './components/overview/ContactTable';
import MetricCard from './components/overview/MetricCard';
import CallHistorySection from './components/overview/CallHistorySection.vue';
import MeetingDemoStatusBar from './components/overview/MeetingDemoStatusBar.vue';
import DailyWorkTasks from './components/overview/DailyWorkTasks.vue';
import { dailyWorkSettingAPI } from 'dashboard/api/dailyWork';
import { OVERVIEW_METRICS, getOverviewMetricDisplayInfo } from './constants';
import ReportHeatmap from './components/Heatmap';
import StateSelectionModalLead from 'dashboard/routes/dashboard/conversation/contact/StateSelectionModalLead.vue';

import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import ApiClient from 'dashboard/api/ApiClient';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { frontendURL } from 'dashboard/helper/URLHelper';
import ContactInfoPanel from '../../contacts/components/ContactInfoPanel.vue';
import alertMixin from 'shared/mixins/alertMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import Multiselect from 'vue-multiselect';
import SetRemainderFollowUpModal from 'dashboard/modules/contact/SetRemainderFollowUpModal.vue';
import MeetingStatusModal from 'dashboard/modules/contact/MeetingStatusModal.vue';
import isAdminMixin from 'dashboard/mixins/isAdmin';
import {
  buildUpdatedAccountPreferences,
  isSameAccountPreference,
  OVERVIEW_LEADS_TABS,
  resolveLastOverviewLeadsTab,
} from 'dashboard/helper/userPreferencesHelper';

const PRIORITY_LEADS_SEGMENT_UI_KEY_PREFIX = 'priority_leads_segment_id_';
const ALL_TASK_USERS_ID = 'all';

// Page size for meetings/reminders tables (default + Load Completed).
const SCHEDULE_PAGE_SIZE = 10;
// Load Completed prefetches the whole today-window; must stay <= controller per_page clamp.
const SCHEDULE_PREFETCH_LIMIT = 200;
const MEETING_DEMO_STATUS_BAR_HEIGHT = 66;

/** YYYY-MM-DD in the browser's local calendar (not UTC — avoids wrong "today" near midnight). */
function formatLocalYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeAgentQuickDateRange(id) {
  const today = new Date();
  let to = today;
  const from = new Date(today);
  switch (id) {
    case 'today':
      break;
    case 'yesterday':
      from.setDate(today.getDate() - 1);
      to = new Date(from);
      break;
    case 'last_7_days':
      from.setDate(today.getDate() - 6);
      break;
    case 'last_30_days':
      from.setDate(today.getDate() - 29);
      break;
    case 'last_3_months':
      from.setMonth(today.getMonth() - 3);
      break;
    case 'last_6_months':
      from.setMonth(today.getMonth() - 6);
      break;
    default:
      break;
  }
  return { dateFrom: formatLocalYmd(from), dateTo: formatLocalYmd(to) };
}

function localYmdToDate(ymd, endOfDayFlag = false) {
  if (!ymd) return null;
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [year, month, day] = parts;
  return endOfDayFlag
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
}

export default {
  name: 'LiveReports',
  components: {
    AgentTable,
    AgentResponseTimeDetailModal,
    ContactTable,
    MetricCard,
    ReportHeatmap,
    StateSelectionModalLead,
    ContactInfoPanel,
    Multiselect,
    CallHistorySection,
    SetRemainderFollowUpModal,
    MeetingStatusModal,
    MeetingDemoStatusBar,
    DailyWorkTasks,
  },
  mixins: [alertMixin, isAdminMixin, uiSettingsMixin],
  data() {
    return {
      pageIndex: 1,
      contactPageIndex: 1,
      items: [],
      remindersMeta: { count: 0, upcomingTotal: 0, currentPage: 1, perPage: SCHEDULE_PAGE_SIZE, completedTodayCount: 0 },
      remindersPageIndex: 1,
      // Meetings data
      meetingItems: [],
      meetingsMeta: { count: 0, upcomingTotal: 0, currentPage: 1, perPage: SCHEDULE_PAGE_SIZE, completedTodayCount: 0 },
      meetingsPageIndex: 1,
      activeScheduleTab: 'meetings',
      hotLeadsItems: [],
      hotLeadsMeta: { count: 0, currentPage: 1, perPage: 20 },
      _isDestroying: false,
      showSegmentModal: false,
      showAgentModal: false,
      selectedHotSegment: null,
      selectedHotSegmentId: '',
      activeLeadsTab: OVERVIEW_LEADS_TABS.TASKS,
      selectedTaskUser: null,
      isLoadingDailyTasks: false,
      Contact: '',
      showContactInfoPanel: false,
      selectedContactId: '',
      // Date range selection for schedule (future dates for meetings/reminders)
      dateRangeOptions: [
        { id: 'today', name: 'Today' },
        { id: 'tomorrow', name: 'Tomorrow' },
        { id: 'next_7_days', name: 'Next 7 days' },
        { id: 'next_30_days', name: 'Next 30 days' },
        { id: 'next_3_months', name: 'Next 3 months' },
        { id: 'next_6_months', name: 'Next 6 months' },
        { id: 'custom', name: 'Custom date range' },
      ],
      selectedDateRange: { id: 'today', name: 'Today' },
      showCustomDatePicker: false,
      customDateFrom: '',
      customDateTo: '',
      isLoadingReminders: false,
      isLoadingMeetings: false,
      isLoadingHotLeads: false,
      /** After "Load completed", merged list is shown; "Hide" refetches upcoming-only */
      showCompletedMeetings: false,
      showCompletedReminders: false,
      trafficHeatmapTypeId: 'contact',
      selectedAgentDateRange: { id: 'today', name: 'Today' },
      showAgentCustomDatePicker: false,
      agentCustomDateFrom: formatLocalYmd(new Date()),
      agentCustomDateTo: formatLocalYmd(new Date()),
      agentAppliedDateRange: computeAgentQuickDateRange('today'),
      showAgentResponseTimeDetail: false,
      selectedArtAgent: null,
      agentDetailSince: null,
      agentDetailUntil: null,
      showReminderFollowUpModal: false,
      showMeetingStatusModal: false,
      meetingStatusContact: null,
      meetingStatusMeetingId: null,
      reminderActionContact: null,
      meetingDemoStatus: { configured: false, doneCount: 0, notDoneCount: 0, veryGoodCount: 0 },
      dailyTasksEnabled: false,
    };
  },
  computed: {
    ...mapGetters({
      agentStatus: 'agents/getAgentStatus',
      agents: 'agents/getAgents',
      accountConversationMetric: 'getAccountConversationMetric',
      agentConversationMetric: 'getAgentConversationMetric',
      accountConversationHeatmap: 'getAccountConversationHeatmapData',
      accountContactHeatmap: 'getAccountContactHeatmapData',
      uiFlags: 'getOverviewUIFlags',
      currentRole: 'getCurrentRole',
      currentUserID: 'getCurrentUserID',
      currentUser: 'getCurrentUser',
    }),
    isTasksTab() {
      return this.activeLeadsTab === OVERVIEW_LEADS_TABS.TASKS;
    },
    isPriorityLeadsTab() {
      return this.activeLeadsTab === OVERVIEW_LEADS_TABS.PRIORITY_LEADS;
    },
    activeLeadsTabIndex() {
      return this.isPriorityLeadsTab ? 1 : 0;
    },
    showLeadsReload() {
      return this.isTasksTab || !!this.selectedHotSegmentId;
    },
    leadsReloadLoading() {
      return this.isTasksTab ? this.isLoadingDailyTasks : this.isLoadingHotLeads;
    },
    showLeadsFullscreen() {
      return this.isTasksTab || !!this.selectedHotSegmentId;
    },
    leadsFullscreenTooltip() {
      return this.isTasksTab ? 'Open full view' : 'Open selected segment';
    },
    taskUserFilterOptions() {
      const users = (this.agents || [])
        .map(user => {
          const id = user.id || user.user_id;
          if (id == null || id === '') return null;
          return {
            id: Number(id),
            name: user.name || user.user?.name || `User #${id}`,
          };
        })
        .filter(Boolean);
      const ownId = this.currentUserID;
      if (ownId && !users.some(user => user.id === Number(ownId))) {
        users.unshift({
          id: Number(ownId),
          name: this.currentUser?.name || 'Me',
        });
      }
      return [{ id: ALL_TASK_USERS_ID, name: 'All Users' }, ...users];
    },
    selectedTaskUserId() {
      return this.selectedTaskUser?.id ?? this.currentUserID ?? null;
    },
    taskUserButtonText() {
      const selected = this.selectedTaskUser;
      if (!selected) return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_AGENT');
      const name = String(selected.name != null ? selected.name : '').trim();
      return name || this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_AGENT');
    },
    taskUserFilterTooltip() {
      const text = this.taskUserButtonText;
      return text && text.length > 12 ? text : null;
    },
    isAgentRole() {
      return this.currentRole === 'agent';
    },
    canManageReminders() {
      return this.isAdmin;
    },
    agentStatusMetrics() {
      let metric = {};
      Object.keys(this.agentStatus).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = getOverviewMetricDisplayInfo(this.agentStatus[key]);
      });
      return metric;
    },
    conversationMetrics() {
      let metric = {};
      Object.keys(this.accountConversationMetric).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = getOverviewMetricDisplayInfo(
          this.accountConversationMetric[key]
        );
      });
      return metric;
    },
    accountScopedBase() {
      const api = new ApiClient('accounts', { accountScoped: true });
      return api.baseUrl();
    },
    showContactViewPane() {
      return this.selectedContactId !== '';
    },
    hotSegments() {
      const list = this.$store.getters[
        'customViews/getCustomViewsByFilterType'
      ]('contact');
      if (!Array.isArray(list) || list.length === 0) return [];
      return list.map(v => ({ id: v.id, name: v.name }));
    },
    priorityLeadsSegmentButtonText() {
      const sel = this.selectedHotSegment;
      if (!sel) return this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_SEGMENT');
      const n = String(sel.name != null ? sel.name : '').trim();
      return n || this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SELECT_SEGMENT');
    },
    /** Full label for tooltip whenever text is long enough to likely need ellipsis */
    segmentFilterTooltip() {
      const text = this.priorityLeadsSegmentButtonText;
      return text && text.length > 12 ? text : null;
    },
    activeScheduleTabIndex() {
      return this.activeScheduleTab === 'reminders' ? 1 : 0;
    },
    reminderTableHeight() {
      return this.showCustomDatePicker ? '308px' : '414px';
    },
    meetingsTableHeight() {
      const base = this.showCustomDatePicker ? 308 : 414;
      if (!this.showMeetingDemoStatusBar) return `${base}px`;
      return `${base - MEETING_DEMO_STATUS_BAR_HEIGHT}px`;
    },
    showMeetingDemoStatusBar() {
      if (this.activeScheduleTab === 'reminders') return false;
      return this.isAdmin || this.meetingDemoStatus.configured;
    },
    /** Fixed table body height for Priority Leads (ContactTable `height` prop). */
    priorityLeadsTableHeight() {
      return '602px';
    },
    todayDate() {
      return formatLocalYmd(new Date());
    },
    /** Avoid empty-then-loader layout jump on first paint (store starts with {}). */
    hasAccountConversationMetricPayload() {
      const m = this.accountConversationMetric;
      if (!m || typeof m !== 'object') return false;
      // Agent scope does not return `unassigned`; only require it for admins.
      const requiredKeys = this.isAgentRole
        ? ['open', 'unattended']
        : ['open', 'unattended', 'unassigned'];
      return requiredKeys.every(k =>
        Object.prototype.hasOwnProperty.call(m, k)
      );
    },
    /**
     * Show loader until we have payload. If data already exists (SPA revisit), keep metrics visible
     * while refetching so the card does not expand/collapse.
     */
    openConversationsMetricCardLoading() {
      const fetching = this.uiFlags.isFetchingAccountConversationMetric;
      const hasPayload = this.hasAccountConversationMetricPayload;
      if (hasPayload && fetching) return false;
      return !hasPayload;
    },
    loadCompletedMeetingsButtonLabel() {
      return this.$t(
        'OVERVIEW_REPORTS.PRIORITY_LEADS.LOAD_COMPLETED_MEETINGS',
        { count: this.meetingsMeta.completedTodayCount || 0 }
      );
    },
    loadCompletedRemindersButtonLabel() {
      return this.$t(
        'OVERVIEW_REPORTS.PRIORITY_LEADS.LOAD_COMPLETED_REMINDERS',
        { count: this.remindersMeta.completedTodayCount || 0 }
      );
    },
    /**
     * Tab badge totals: upcoming + today's completed.
     * Independent of pagination's `count` (which equals only the currently-paginated
     * list), so switching between default mode and "Load Completed" mode does not
     * change the badge number.
     */
    meetingsTabCount() {
      return (
        (this.meetingsMeta.upcomingTotal || 0) +
        (this.meetingsMeta.completedTodayCount || 0)
      );
    },
    remindersTabCount() {
      return (
        (this.remindersMeta.upcomingTotal || 0) +
        (this.remindersMeta.completedTodayCount || 0)
      );
    },
    /**
     * In default (upcoming-only) mode the server already paginates `meetingItems`,
     * so we pass them through. After "Load Completed" we hold the full merged list
     * in `meetingItems` and slice it here for client-side pagination.
     */
    displayMeetingItems() {
      if (!this.showCompletedMeetings) return this.meetingItems;
      const pageSize = this.meetingsMeta.perPage || SCHEDULE_PAGE_SIZE;
      const pageIndex = this.meetingsPageIndex || 1;
      const start = (pageIndex - 1) * pageSize;
      return this.meetingItems.slice(start, start + pageSize);
    },
    /** Same client-side pagination strategy as displayMeetingItems, for reminders. */
    displayReminderItems() {
      if (!this.showCompletedReminders) return this.items;
      const pageSize = this.remindersMeta.perPage || SCHEDULE_PAGE_SIZE;
      const pageIndex = this.remindersPageIndex || 1;
      const start = (pageIndex - 1) * pageSize;
      return this.items.slice(start, start + pageSize);
    },
    trafficHeatmapOptions() {
      return [
        {
          id: 'contact',
          name: this.$t('OVERVIEW_REPORTS.TRAFFIC_HEATMAP.TYPE_CONTACT'),
        },
        {
          id: 'conversation',
          name: this.$t('OVERVIEW_REPORTS.TRAFFIC_HEATMAP.TYPE_CONVERSATION'),
        },
      ];
    },
    currentTrafficHeatmapOption() {
      return (
        this.trafficHeatmapOptions.find(o => o.id === this.trafficHeatmapTypeId) ||
        this.trafficHeatmapOptions[0]
      );
    },
    trafficHeatmapHeatData() {
      return this.trafficHeatmapTypeId === 'contact'
        ? this.accountContactHeatmap
        : this.accountConversationHeatmap;
    },
    trafficHeatmapLoading() {
      return this.trafficHeatmapTypeId === 'contact'
        ? this.uiFlags.isFetchingAccountContactsHeatmap
        : this.uiFlags.isFetchingAccountConversationsHeatmap;
    },
    trafficHeatmapEntityType() {
      return this.trafficHeatmapTypeId === 'contact' ? 'contact' : 'conversation';
    },
    agentDateRangeOptions() {
      return [
        { id: 'today', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.TODAY') },
        { id: 'yesterday', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.YESTERDAY') },
        { id: 'last_7_days', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.LAST_7_DAYS') },
        { id: 'last_30_days', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.LAST_30_DAYS') },
        { id: 'last_3_months', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.LAST_3_MONTHS') },
        { id: 'last_6_months', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.LAST_6_MONTHS') },
        { id: 'custom', name: this.$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.QUICK_DATE_RANGE.CUSTOM') },
      ];
    },
    agentTodayYmd() {
      return formatLocalYmd(new Date());
    },
  },

  watch: {
    selectedContactId() {
      this.selectedContact();
    },
    hotSegments(newList) {
      this.syncPriorityLeadsSegmentWithStoreList(newList);
    },
    taskUserFilterOptions(options) {
      this.syncSelectedTaskUserWithOptions(options);
    },
    '$route.params.accountId'() {
      this.loadDailyTasksEnabled();
      this.restoreOverviewLeadsTab();
      this.restorePriorityLeadsFilters().then(() => {
        this.syncPriorityLeadsSegmentWithStoreList(this.hotSegments);
        this.fetchHotLeads();
      });
    },
  },

  created() {
    this.restoreOverviewLeadsTab();
  },

  async mounted() {
    this.ensureSelectedTaskUser();
    this.loadDailyTasksEnabled();
    this.$store.dispatch('agents/get');
    await this.$store.dispatch('customViews/get', 'contact');
    this.fetchAllData();

    bus.$on('fetch_overview_reports', () => {
      this.fetchAllData();
    });

    await this.restorePriorityLeadsFilters();
    this.syncPriorityLeadsSegmentWithStoreList(this.hotSegments);
    this.fetchHotLeads();

    this.$nextTick(() => {
      this.syncPriorityLeadsSegmentWithStoreList(this.hotSegments);
    });

    this.fetch();
    this.fetchMeetings();
    bus.$on('reloaad-pipeline', this.onContactCrmUpdated);
    bus.$on('reload-contact-updated', this.onContactCrmUpdated);
    bus.$on('on-assign-agent', this.onOverviewContactAssigneeUpdated);
    bus.$on('set-reminder-updated', this.fetch);
    bus.$on('demo-meeting-updated', this.onDemoMeetingUpdated);
  },
  beforeDestroy() {
    this._isDestroying = true;
    bus.$off('fetch_overview_reports');
    bus.$off('reloaad-pipeline', this.onContactCrmUpdated);
    bus.$off('reload-contact-updated', this.onContactCrmUpdated);
    bus.$off('on-assign-agent', this.onOverviewContactAssigneeUpdated);
    bus.$off('set-reminder-updated', this.fetch);
    bus.$off('demo-meeting-updated', this.onDemoMeetingUpdated);
  },
  methods: {
    ...mapActions('globalModals', ['showSetReminderModal', 'showScheduleDemoModal']),
    onRescheduleMeeting(contact) {
      if (!contact?.id) return;
      this.showScheduleDemoModal({
        ...contact,
        additional_attributes: { ...(contact.additional_attributes || {}) },
        meeting_lifecycle: contact.meeting_lifecycle || 'future',
        can_reschedule_meeting: true,
        can_cancel_meeting: true,
        schedule_intent: 'reschedule',
      });
    },
    async onCancelMeeting(contact) {
      const meetingId = contact?.primary_contact_meeting_id;
      if (!contact?.id || !meetingId) {
        this.showAlert(this.$t('EDIT_CONTACT.CANCEL_MEETING_MISSING'));
        return;
      }
      const confirmed = await this.$refs.cancelMeetingDialog?.showConfirmation();
      if (!confirmed) return;
      try {
        await this.$store.dispatch('contacts/cancel_demo_meeting', {
          contactId: contact.id,
          meetingId,
        });
        this.showAlert(this.$t('EDIT_CONTACT.CANCEL_MEETING_SUCCESS'));
        this.fetchMeetings();
      } catch (error) {
        this.showAlert(
          error.response?.data?.error ||
            error.message ||
            this.$t('EDIT_CONTACT.CANCEL_MEETING_ERROR')
        );
      }
    },
    async loadDailyTasksEnabled() {
      try {
        const { data } = await dailyWorkSettingAPI.status();
        this.dailyTasksEnabled = Boolean(data?.enabled);
      } catch (_) {
        this.dailyTasksEnabled = false;
      }
    },
    selectedContact() {
      if (!this.selectedContactId) {
        this.Contact = '';
        return undefined;
      }
      const matchId = item =>
        String(item?.id) === String(this.selectedContactId);
      const contact =
        this.hotLeadsItems.find(matchId) ||
        this.items.find(matchId) ||
        this.meetingItems.find(matchId);
      this.Contact = contact || { id: this.selectedContactId };
      return undefined;
    },
    openContactInfoPanel(contactId) {
      this.selectedContactId = contactId;
      this.selectedContact();
      this.showContactInfoPanelPane = true;
    },
    closeContactInfoPanel() {
      console.log('this is called during closing');
      this.selectedContactId = '';
      this.showContactInfoPanelPane = false;
    },
    onContactDeleted() {
      this.selectedContactId = '';
      // const currentPage = this.meta.currentPage || 1;
      // const totalCount = this.meta.count || 0;
      // const pageSize = 15;
      // const totalPages = Math.ceil(totalCount / pageSize);
      // if (this.records.length === 0 && currentPage > 1) {
        // const prevPage = currentPage - 1;
        // this.$router.push({
        //   path: this.$route.path,
        //   query: { ...this.$route.query, page: prevPage },
        // });
        // this.fetchContacts(prevPage);
      // } else {
        // this.fetchContacts(currentPage);
      // }
    },
    fetchAllData() {
      this.fetchAccountConversationMetric();
      if (!this.isAgentRole) {
        this.fetchAgentConversationMetric();
      }
      this.fetchTrafficHeatmapData();
      this.fetchContactsList();
    },
    onTrafficHeatmapTypeSelect(option) {
      if (!option || this.trafficHeatmapTypeId === option.id) {
        return;
      }
      this.trafficHeatmapTypeId = option.id;
      this.fetchTrafficHeatmapData();
    },
    downloadTrafficHeatmapData() {
      // Same unix window as Overview heatmap initial load so CSV matches UI on any host (Mac/staging).
      const to = endOfDay(new Date());
      const from = startOfDay(subDays(to, 6));
      const payload = {
        to: getUnixTime(to),
        since: getUnixTime(from),
        until: getUnixTime(to),
        // Scope CSV to the current agent when an agent is viewing the overview.
        ...(this.isAgentRole && {
          type: 'agent',
          id: this.currentUserID,
        }),
      };

      if (this.trafficHeatmapTypeId === 'contact') {
        this.$store.dispatch('downloadAccountContactHeatmap', payload);
      } else {
        this.$store.dispatch('downloadAccountConversationHeatmap', payload);
      }
    },
    fetchTrafficHeatmapData() {
      if (this.trafficHeatmapTypeId === 'contact') {
        if (this.uiFlags.isFetchingAccountContactsHeatmap) {
          return;
        }
      } else if (this.uiFlags.isFetchingAccountConversationsHeatmap) {
        return;
      }

      // the data for the last 6 days won't ever change,
      // so there's no need to fetch it again
      // but we can write some logic to check if the data is already there
      // if it is there, we can refetch data only for today all over again
      // and reconcile it with the rest of the data
      // this will reduce the load on the server doing number crunching
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));

      const existingHeatmap =
        this.trafficHeatmapTypeId === 'contact'
          ? this.accountContactHeatmap
          : this.accountConversationHeatmap;

      if (existingHeatmap.length) {
        to = endOfDay(new Date());
        from = startOfDay(to);
      }

      const basePayload = {
        from: getUnixTime(from),
        to: getUnixTime(to),
        groupBy: 'hour',
        businessHours: false,
        // Scope traffic to the current agent when an agent is viewing the overview;
        // admins keep the default account-wide scope.
        ...(this.isAgentRole && {
          type: 'agent',
          id: this.currentUserID,
        }),
      };

      if (this.trafficHeatmapTypeId === 'contact') {
        this.$store.dispatch('fetchAccountContactHeatmap', {
          ...basePayload,
          metric: 'contacts_count',
        });
      } else {
        this.$store.dispatch('fetchAccountConversationHeatmap', {
          ...basePayload,
          metric: 'conversations_count',
        });
      }
    },
    fetchAccountConversationMetric() {
      // Agents see only their own scoped metrics; admins see the full account scope.
      const payload = this.isAgentRole
        ? { type: 'agent', id: this.currentUserID }
        : { type: 'account' };
      this.$store.dispatch('fetchAccountConversationMetric', payload);
    },
    fetchAgentConversationMetric() {
      const { since, until } = this.getAgentDateRangeUnix();
      this.$store.dispatch('fetchAgentConversationMetric', {
        type: 'agent',
        page: this.pageIndex,
        since,
        until,
      });
    },
    getAgentDateRangeUnix() {
      const fromDate = localYmdToDate(this.agentAppliedDateRange.dateFrom, false);
      const toDate = localYmdToDate(this.agentAppliedDateRange.dateTo, true);
      if (!fromDate || !toDate) {
        return {
          since: getUnixTime(startOfDay(new Date())),
          until: getUnixTime(endOfDay(new Date())),
        };
      }
      return {
        since: getUnixTime(startOfDay(fromDate)),
        until: getUnixTime(endOfDay(toDate)),
      };
    },
    onAgentDateRangeSelect(selected) {
      this.selectedAgentDateRange = selected;
      if (selected.id === 'custom') {
        this.showAgentCustomDatePicker = true;
        this.agentCustomDateFrom = this.agentAppliedDateRange.dateFrom || this.agentTodayYmd;
        this.agentCustomDateTo = this.agentAppliedDateRange.dateTo || this.agentTodayYmd;
        return;
      }
      this.showAgentCustomDatePicker = false;
      this.agentAppliedDateRange = computeAgentQuickDateRange(selected.id);
      this.pageIndex = 1;
      this.fetchAgentConversationMetric();
    },
    onAgentFromDateChange() {
      if (this.agentCustomDateFrom && this.agentCustomDateFrom > this.agentTodayYmd) {
        this.agentCustomDateFrom = this.agentTodayYmd;
      }
      if (this.agentCustomDateTo && this.agentCustomDateFrom > this.agentCustomDateTo) {
        this.agentCustomDateTo = this.agentCustomDateFrom;
      }
    },
    onAgentToDateChange() {
      if (this.agentCustomDateTo && this.agentCustomDateTo > this.agentTodayYmd) {
        this.agentCustomDateTo = this.agentTodayYmd;
      }
      if (this.agentCustomDateFrom && this.agentCustomDateTo < this.agentCustomDateFrom) {
        this.agentCustomDateTo = this.agentCustomDateFrom;
      }
    },
    applyAgentCustomDateRange() {
      if (!this.agentCustomDateFrom || !this.agentCustomDateTo) {
        this.showAlert('Please select both start and end dates');
        return;
      }
      if (this.agentCustomDateTo < this.agentCustomDateFrom) {
        this.showAlert('To date must be greater than or equal to From date');
        return;
      }
      if (this.agentCustomDateFrom > this.agentTodayYmd || this.agentCustomDateTo > this.agentTodayYmd) {
        return;
      }
      this.agentAppliedDateRange = {
        dateFrom: this.agentCustomDateFrom,
        dateTo: this.agentCustomDateTo,
      };
      this.pageIndex = 1;
      this.fetchAgentConversationMetric();
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.fetchAgentConversationMetric();
    },
    openAgentResponseTimeDetail(agent) {
      if (!agent?.id) return;
      const { since, until } = this.getAgentDateRangeUnix();
      this.selectedArtAgent = agent;
      this.agentDetailSince = since;
      this.agentDetailUntil = until;
      this.showAgentResponseTimeDetail = true;
    },
    closeAgentResponseTimeDetail() {
      this.showAgentResponseTimeDetail = false;
      this.selectedArtAgent = null;
      this.agentDetailSince = null;
      this.agentDetailUntil = null;
    },
    onUpdateReminder(contact) {
      if (!this.canManageReminders || !contact?.id) return;
      this.showSetReminderModal({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phone_number: contact.phone_number,
        additional_attributes: { ...(contact.additional_attributes || {}) },
        custom_attributes: { ...(contact.custom_attributes || {}) },
      });
    },
    onCompleteReminder(contact) {
      if (!this.canManageReminders || !contact?.id) return;
      this.reminderActionContact = {
        id: contact.id,
        name: contact.name,
        additional_attributes: { ...(contact.additional_attributes || {}) },
      };
      this.showReminderFollowUpModal = true;
    },
    closeReminderFollowUpModal() {
      this.showReminderFollowUpModal = false;
      this.reminderActionContact = null;
    },
    onReminderFollowUpSuccess() {
      this.closeReminderFollowUpModal();
    },
    onMeetingStatus(contact) {
      const meetingId = contact?.primary_contact_meeting_id;
      if (!contact?.id || !meetingId) return;
      this.meetingStatusContact = {
        id: contact.id,
        name: contact.name,
        additional_attributes: { ...(contact.additional_attributes || {}) },
        demo_status_recorded: !!contact.demo_status_recorded,
        demo_completed: contact.demo_completed,
        demo_description: contact.demo_description || '',
        very_good_demo: contact.very_good_demo,
      };
      this.meetingStatusMeetingId = meetingId;
      this.showMeetingStatusModal = true;
    },
    closeMeetingStatusModal() {
      this.showMeetingStatusModal = false;
    },
    onMeetingStatusSuccess() {
      this.closeMeetingStatusModal();
      this.refreshMeetingsAfterDemoUpdate();
    },
    async onDemoMeetingUpdated() {
      await this.refreshMeetingsAfterDemoUpdate();
    },
    async refreshMeetingsAfterDemoUpdate() {
      if (this.showCompletedMeetings) {
        await this.loadCompletedMeetings();
      } else {
        await this.fetchMeetings();
      }
    },
    toggleSegmentModal() {
      this.showSegmentModal = !this.showSegmentModal;
    },
    toggleAgentModal() {
      this.showAgentModal = !this.showAgentModal;
    },
    ensureSelectedTaskUser() {
      if (this.selectedTaskUser) {
        this.syncSelectedTaskUserWithOptions(this.taskUserFilterOptions);
        return;
      }
      const ownId = this.currentUserID;
      this.selectedTaskUser = {
        id: ownId == null || ownId === '' ? null : Number(ownId),
        name: this.currentUser?.name || 'Me',
      };
      this.syncSelectedTaskUserWithOptions(this.taskUserFilterOptions);
    },
    syncSelectedTaskUserWithOptions(options) {
      const selectedId = this.selectedTaskUser?.id;
      if (selectedId == null || selectedId === ALL_TASK_USERS_ID) return;
      const match = (options || []).find(
        option => option.id === Number(selectedId)
      );
      if (match && this.selectedTaskUser !== match) this.selectedTaskUser = match;
    },
    async onLeadsTabChange(index) {
      const previousTab = this.activeLeadsTab;
      const nextTab =
        index === 1
          ? OVERVIEW_LEADS_TABS.PRIORITY_LEADS
          : OVERVIEW_LEADS_TABS.TASKS;

      this.activeLeadsTab = nextTab;
      this.showSegmentModal = false;
      this.showAgentModal = false;

      const saved = await this.persistOverviewLeadsTab(nextTab);
      if (saved === false) {
        this.activeLeadsTab = previousTab;
        this.showAlert(
          this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.TAB_SAVE_FAILED')
        );
      }
    },
    restoreOverviewLeadsTab() {
      const accountId = this.priorityLeadsAccountId();
      if (!accountId) return;

      const savedTab =
        this.uiSettings?.user_preferences_by_account?.[accountId]
          ?.last_overview_leads_tab;
      const { tab } = resolveLastOverviewLeadsTab(savedTab);
      this.activeLeadsTab = tab;
    },
    async persistOverviewLeadsTab(tab = this.activeLeadsTab) {
      const accountId = this.priorityLeadsAccountId();
      if (!accountId) return true;

      const currentUser = this.currentUser;
      if (
        isSameAccountPreference(
          currentUser,
          accountId,
          'last_overview_leads_tab',
          tab
        )
      ) {
        return true;
      }

      const { uiSettings } = buildUpdatedAccountPreferences(
        accountId,
        currentUser,
        'last_overview_leads_tab',
        tab
      );
      return this.$store.dispatch('updateUISettings', { uiSettings });
    },
    handleTaskUserSelection(user) {
      this.selectedTaskUser = user;
      this.showAgentModal = false;
      this.$nextTick(() => {
        this.$refs.dailyWorkTasks?.reloadTasks?.();
      });
    },
    reloadActiveLeadsTab() {
      if (this.isTasksTab) {
        this.$refs.dailyWorkTasks?.reloadTasks?.();
        return;
      }
      this.reloadHotLeads();
    },
    openActiveLeadsFullscreen() {
      if (this.isTasksTab) {
        this.$refs.dailyWorkTasks?.openFullView?.();
        return;
      }
      this.openSelectedSegment();
    },

    resetPriorityLeadsInvalidSegmentFilter() {
      if (this._isDestroying) return;

      this.selectedHotSegment = null;
      this.selectedHotSegmentId = '';
      this.contactPageIndex = 1;
      this.persistPriorityLeadsSegment();
      this.fetchHotLeads();
    },

    syncPriorityLeadsSegmentWithStoreList(segments) {
      if (!this.selectedHotSegmentId || this._isDestroying) {
        return;
      }

      const { isFetching } =
        this.$store.getters['customViews/getUIFlags'] || {};
      if (isFetching) return;

      const list = Array.isArray(segments) ? segments : this.hotSegments || [];
      if (list.length === 0) {
        this.$store.dispatch('customViews/get', 'contact');
        return;
      }

      const seg = list.find(
        s => s && String(s.id) === String(this.selectedHotSegmentId)
      );
      if (!seg) {
        this.resetPriorityLeadsInvalidSegmentFilter();
      } else {
        this.selectedHotSegment = { id: seg.id, name: seg.name };
      }
    },

    priorityLeadsAccountId() {
      const accountId =
        this.$route.params.accountId ?? this.$route.params.account_id;
      if (accountId == null || String(accountId).trim() === '') return null;
      return String(accountId);
    },

    priorityLeadsUiSettingsKey() {
      const accountId = this.priorityLeadsAccountId();
      if (!accountId) return null;
      return `${PRIORITY_LEADS_SEGMENT_UI_KEY_PREFIX}${accountId}`;
    },

    priorityLeadsLocalStorageKey() {
      const accountId = this.priorityLeadsAccountId();
      if (!accountId) return null;
      return `${LOCAL_STORAGE_KEYS.PRIORITY_LEADS_FILTERS_PREFIX}${accountId}`;
    },

    normalizedPriorityLeadsSegmentId() {
      if (
        this.selectedHotSegmentId == null ||
        String(this.selectedHotSegmentId).trim() === ''
      ) {
        return '';
      }
      return String(this.selectedHotSegmentId);
    },

    async persistPriorityLeadsSegment() {
      const accountId = this.priorityLeadsAccountId();
      if (!accountId) return true;

      const segmentId = this.normalizedPriorityLeadsSegmentId();
      const normalizedValue = segmentId || null;
      const currentUser = this.currentUser;

      if (
        isSameAccountPreference(
          currentUser,
          accountId,
          'priority_leads_segment_id',
          normalizedValue
        )
      ) {
        return true;
      }

      const { uiSettings } = buildUpdatedAccountPreferences(
        accountId,
        currentUser,
        'priority_leads_segment_id',
        normalizedValue
      );
      const saved = await this.$store.dispatch('updateUISettings', {
        uiSettings,
      });
      if (saved === false) {
        this.showAlert(
          this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.SEGMENT_SAVE_FAILED')
        );
        return false;
      }
      this.clearLegacyPriorityLeadsLocalStorage();
      return true;
    },

    applyRestoredPriorityLeadsSegmentId(segmentId) {
      this.selectedHotSegmentId = String(segmentId);
      const segments = this.hotSegments || [];
      const seg = segments.find(
        s => s.id != null && String(s.id) === String(segmentId)
      );
      this.selectedHotSegment = seg || {
        id: segmentId,
        name: '',
      };
    },

    readLegacyPriorityLeadsSegmentFromLocalStorage() {
      const key = this.priorityLeadsLocalStorageKey();
      if (!key || typeof localStorage === 'undefined') return '';
      let parsed;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return '';
        parsed = JSON.parse(raw);
      } catch {
        return '';
      }
      let segmentId = parsed?.segmentId;
      if (segmentId == null || segmentId === '') {
        if (
          parsed?.filter === 'segment' &&
          parsed.segmentId != null &&
          parsed.segmentId !== ''
        ) {
          segmentId = parsed.segmentId;
        }
      }
      if (segmentId == null || String(segmentId).trim() === '') return '';
      return String(segmentId);
    },

    clearLegacyPriorityLeadsLocalStorage() {
      const key = this.priorityLeadsLocalStorageKey();
      if (!key || typeof localStorage === 'undefined') return;
      try {
        localStorage.removeItem(key);
      } catch {
      }
    },

    async restorePriorityLeadsFilters() {
      const accountId = this.priorityLeadsAccountId();
      if (!accountId) return;

      const legacyKey = this.priorityLeadsUiSettingsKey();
      const fromAccountPrefs =
        this.uiSettings?.user_preferences_by_account?.[accountId]
          ?.priority_leads_segment_id;
      const fromLegacy =
        legacyKey && this.uiSettings ? this.uiSettings[legacyKey] : '';

      let segmentId = '';
      if (fromAccountPrefs != null && String(fromAccountPrefs).trim() !== '') {
        segmentId = String(fromAccountPrefs);
      } else if (fromLegacy != null && String(fromLegacy).trim() !== '') {
        segmentId = String(fromLegacy);
      }

      if (!segmentId) {
        segmentId = this.readLegacyPriorityLeadsSegmentFromLocalStorage();
        if (segmentId) {
          this.applyRestoredPriorityLeadsSegmentId(segmentId);
          await this.persistPriorityLeadsSegment();
          return;
        }
        return;
      }

      this.applyRestoredPriorityLeadsSegmentId(segmentId);
      this.clearLegacyPriorityLeadsLocalStorage();
      if (
        fromLegacy != null &&
        String(fromLegacy).trim() !== '' &&
        (fromAccountPrefs == null || String(fromAccountPrefs).trim() === '')
      ) {
        await this.persistPriorityLeadsSegment();
      }
    },

    async handleSegmentSelection(segment) {
      this.selectedHotSegment = segment;
      this.selectedHotSegmentId =
        segment && segment.id != null ? String(segment.id) : '';

      this.contactPageIndex = 1;
      await this.persistPriorityLeadsSegment();
      this.fetchHotLeads();
    },

    async openSelectedSegment() {
      if (!this.selectedHotSegmentId) return;
      const accountId =
        this.$route.params.accountId ?? this.$route.params.account_id;
      if (accountId == null || String(accountId).trim() === '') return;

      await this.$store.dispatch('customViews/get', 'contact');

      await this.$router.push(
        frontendURL(
          `accounts/${accountId}/contacts/custom_view/${this.selectedHotSegmentId}`
        )
      );
    },

    /**
     * Priority Leads: segment (custom view) only — no request until a segment is chosen.
     * @param {number|undefined} pageIndex — omit for first page
     */
    buildHotLeadsRequestParams(pageIndex) {
      const params = { limit: this.hotLeadsMeta.perPage };
      if (pageIndex != null) {
        params.page = pageIndex;
      }

      if (
        !this.selectedHotSegmentId ||
        String(this.selectedHotSegmentId).trim() === ''
      ) {
        return null;
      }
      params.custom_filter_id = this.selectedHotSegmentId;
      return params;
    },

    async fetchHotLeads() {
      try {
        this.isLoadingHotLeads = true;
        const params = this.buildHotLeadsRequestParams();
        if (!params) {
          this.hotLeadsItems = [];
          this.hotLeadsMeta.count = 0;
          return;
        }

        const { data } = await axios.get(`${this.accountScopedBase}/hot_leads`, { params });
        this.hotLeadsItems = data?.data || [];
        this.hotLeadsMeta = { count: data?.meta?.total_count || 0, currentPage: data?.meta?.page || 1, perPage: data?.meta?.per_page || 20 };

      } catch (e) {
        this.hotLeadsItems = [];
        this.hotLeadsMeta.count = 0;
      } finally {
    this.isLoadingHotLeads = false;
      }
    },
    async reloadHotLeads() {
      if (this.isLoadingHotLeads) return;
      const currentPage = this.hotLeadsMeta.currentPage || this.contactPageIndex || 1;
      if (currentPage > 1) {
        await this.onContactPageChange(currentPage);
        return;
      }
      await this.fetchHotLeads();
    },
    fetchContactsList() {
      this.$store.dispatch('contacts/get', {
        page: this.contactPageIndex,
        sortAttr: 'name',
      });
    },
   async onContactPageChange(pageIndex) {
      try {
        this.isLoadingHotLeads = true;
        const params = this.buildHotLeadsRequestParams(pageIndex);
        if (!params) {
          this.hotLeadsItems = [];
          this.hotLeadsMeta.count = 0;
          return;
        }
        const { data } = await axios.get(`${this.accountScopedBase}/hot_leads`, { params });
        this.hotLeadsItems = data?.data || [];
        this.hotLeadsMeta = { count: data?.meta?.total_count || 0, currentPage: data?.meta?.page || 1, perPage: data?.meta?.per_page || 20 };

      } catch (e) {
        this.hotLeadsItems = [];
        this.hotLeadsMeta.count = 0;
      } finally {
        this.isLoadingHotLeads = false;
     }
    },
    async onRemindersPageChange(pageIndex) {
      // Client-side pagination over the merged (completed + upcoming) list.
      // Refetching here would drop the "Load Completed" state and reset to upcoming-only.
      if (this.showCompletedReminders) {
        this.remindersPageIndex = pageIndex;
        this.remindersMeta = { ...this.remindersMeta, currentPage: pageIndex };
        return;
      }
      try {
        this.isLoadingReminders = true;
        this.remindersPageIndex = pageIndex;
        const params = {
          page: pageIndex,
          ...this.scheduleQueryBase(false),
        };
        const { data } = await axios.get(`${this.accountScopedBase}/today_reminders`, { params });
        this.items = (data.upcoming?.data) || [];
        this.showCompletedReminders = false;
        const upcomingTotal = data?.upcoming?.total_count || 0;
        const completedTotal = data?.past?.total_count || 0;
        this.remindersMeta = {
          count: upcomingTotal,
          upcomingTotal,
          currentPage: data?.page || 1,
          perPage: data?.per_page || SCHEDULE_PAGE_SIZE,
          completedTodayCount: completedTotal,
        };
      } catch (e) {
        this.items = [];
      } finally {
        this.isLoadingReminders = false;
      }
    },
    async fetch() {
      try {
        this.isLoadingReminders = true;
        // fetch() always returns page 1; reset the index so ve-pagination doesn't desync.
        this.remindersPageIndex = 1;
        const params = this.scheduleQueryBase(false);
        const { data } = await axios.get(`${this.accountScopedBase}/today_reminders`, { params });
        this.items = (data.upcoming?.data) || [];
        this.showCompletedReminders = false;
        const upcomingTotal = data?.upcoming?.total_count || 0;
        const completedTotal = data?.past?.total_count || 0;
        this.remindersMeta = {
          count: upcomingTotal,
          upcomingTotal,
          currentPage: data?.page || 1,
          perPage: data?.per_page || SCHEDULE_PAGE_SIZE,
          completedTodayCount: completedTotal,
        };
      } catch (e) {
        this.items = [];
      } finally {
        this.isLoadingReminders = false;
      }
    },
    applyMeetingDemoStatus(data) {
      const demoStatus = data?.demo_status;
      if (!demoStatus) return;
      this.meetingDemoStatus = {
        configured: !!demoStatus.configured,
        doneCount: demoStatus.done_count || 0,
        notDoneCount: demoStatus.not_done_count || 0,
        veryGoodCount: demoStatus.very_good_count || 0,
      };
    },
    clearMeetingsOnError() {
      this.meetingItems = [];
      this.meetingDemoStatus = { configured: false, doneCount: 0, notDoneCount: 0, veryGoodCount: 0 };
    },
    onContactCrmUpdated() {
      this.fetchHotLeads();
      this.fetchMeetings();
    },
    onOverviewContactAssigneeUpdated(payload) {
      const contactId = payload?.contactId;
      const agent = payload?.agent;
      if (!contactId || !agent?.id) {
        return;
      }
      const patch = {
        assignee_id: agent.id,
        assignee_name: agent.name,
        assignee: agent,
      };
      const patchList = list =>
        (list || []).map(item =>
          String(item?.id) === String(contactId) ? { ...item, ...patch } : item
        );
      this.items = patchList(this.items);
      this.hotLeadsItems = patchList(this.hotLeadsItems);
      this.meetingItems = patchList(this.meetingItems);
      if (String(this.selectedContactId) === String(contactId)) {
        this.Contact = {
          ...(this.Contact || { id: contactId }),
          ...patch,
        };
      }
    },
    async fetchMeetings() {
      try {
        this.isLoadingMeetings = true;
        // fetchMeetings() always returns page 1; reset the index so ve-pagination doesn't desync.
        this.meetingsPageIndex = 1;
        const params = this.scheduleQueryBase(false);
        const { data } = await axios.get(`${this.accountScopedBase}/today_meetings`, { params });
        this.applyMeetingDemoStatus(data);
        this.meetingItems = (data.upcoming?.data) || [];
        this.showCompletedMeetings = false;
        const upcomingTotal = data?.upcoming?.total_count || 0;
        const completedTotal = data?.past?.total_count || 0;
        this.meetingsMeta = {
          count: upcomingTotal,
          upcomingTotal,
          currentPage: data?.page || 1,
          perPage: data?.per_page || SCHEDULE_PAGE_SIZE,
          completedTodayCount: completedTotal,
        };
      } catch (e) {
        this.clearMeetingsOnError();
      } finally {
        this.isLoadingMeetings = false;
      }
    },
    async onMeetingsPageChange(pageIndex) {
      // Client-side pagination over the merged (completed + upcoming) list.
      // Refetching here would drop the "Load Completed" state and reset to upcoming-only.
      if (this.showCompletedMeetings) {
        this.meetingsPageIndex = pageIndex;
        this.meetingsMeta = { ...this.meetingsMeta, currentPage: pageIndex };
        return;
      }
      try {
        this.isLoadingMeetings = true;
        this.meetingsPageIndex = pageIndex;
        const params = {
          page: pageIndex,
          ...this.scheduleQueryBase(false),
        };
        const { data } = await axios.get(`${this.accountScopedBase}/today_meetings`, { params });
        this.applyMeetingDemoStatus(data);
        this.meetingItems = (data.upcoming?.data) || [];
        this.showCompletedMeetings = false;
        const upcomingTotal = data?.upcoming?.total_count || 0;
        const completedTotal = data?.past?.total_count || 0;
        this.meetingsMeta = {
          count: upcomingTotal,
          upcomingTotal,
          currentPage: data?.page || 1,
          perPage: data?.per_page || SCHEDULE_PAGE_SIZE,
          completedTodayCount: completedTotal,
        };
      } catch (e) {
        this.clearMeetingsOnError();
      } finally {
        this.isLoadingMeetings = false;
      }
    },
    onScheduleTabChange(index) {
      this.activeScheduleTab = index === 1 ? 'reminders' : 'meetings';
    },
    onDateRangeChange(selected) {
      this.selectedDateRange = selected;
      if (selected.id === 'custom') {
        this.showCustomDatePicker = true;
        // Set default custom dates (from today to next week)
        const today = new Date();
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        this.customDateFrom = formatLocalYmd(today);
        this.customDateTo = formatLocalYmd(nextWeek);
      } else {
        this.showCustomDatePicker = false;
        this.fetchScheduleData();
      }
    },
    onFromDateChange() {
      // If To date is less than From date, update To date to match From date
      if (this.customDateTo && this.customDateFrom > this.customDateTo) {
        this.customDateTo = this.customDateFrom;
      }
    },
    applyCustomDateRange() {
      if (!this.customDateFrom || !this.customDateTo) {
        this.showAlert('Please select both start and end dates');
        return;
      }

      // Validate that To date is greater than or equal to From date
      if (this.customDateTo < this.customDateFrom) {
        this.showAlert('To date must be greater than or equal to From date');
        return;
      }

      // Keep the custom date picker visible, just fetch the data
      this.fetchScheduleData();
    },
    /** Browser's IANA timezone — sent to the backend so "Today" matches the user's clock. */
    browserTimeZone() {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
      } catch (e) {
        return 'Asia/Kolkata';
      }
    },
    /** Shared query flags for today_reminders / today_meetings. Pass false to skip loading past rows (counts only). */
    scheduleQueryBase(includePastRecords) {
      return {
        ...this.getDateRangeParams(),
        time_zone: this.browserTimeZone(),
        past_page: 1,
        include_past_records: includePastRecords,
      };
    },
    getDateRangeParams() {
      const params = {};
      const today = new Date();
      let fromDate, toDate;

      if (this.selectedDateRange.id === 'custom') {
        fromDate = this.customDateFrom;
        toDate = this.customDateTo;
      } else {
        // For future dates, fromDate is today and toDate is calculated (local calendar dates)
        fromDate = formatLocalYmd(today);
        let calculatedDate;
        switch (this.selectedDateRange.id) {
          case 'today':
            toDate = formatLocalYmd(today);
            break;
          case 'tomorrow':
            calculatedDate = new Date(today);
            calculatedDate.setDate(calculatedDate.getDate() + 1);
            fromDate = formatLocalYmd(calculatedDate);
            toDate = formatLocalYmd(calculatedDate);
            break;
          case 'next_7_days':
            calculatedDate = new Date(today);
            calculatedDate.setDate(calculatedDate.getDate() + 7);
            toDate = formatLocalYmd(calculatedDate);
            break;
          case 'next_30_days':
            calculatedDate = new Date(today);
            calculatedDate.setDate(calculatedDate.getDate() + 30);
            toDate = formatLocalYmd(calculatedDate);
            break;
          case 'next_3_months':
            calculatedDate = new Date(today);
            calculatedDate.setMonth(calculatedDate.getMonth() + 3);
            toDate = formatLocalYmd(calculatedDate);
            break;
          case 'next_6_months':
            calculatedDate = new Date(today);
            calculatedDate.setMonth(calculatedDate.getMonth() + 6);
            toDate = formatLocalYmd(calculatedDate);
            break;
          default:
            calculatedDate = new Date(today);
            calculatedDate.setDate(calculatedDate.getDate() + 7);
            toDate = formatLocalYmd(calculatedDate);
        }
      }

      params.from_date = fromDate;
      params.to_date = toDate;
      return params;
    },
    fetchScheduleData() {
      this.showCompletedMeetings = false;
      this.showCompletedReminders = false;
      this.fetch();
      this.fetchMeetings();
    },
    reloadScheduleData() {
      this.fetchScheduleData();
    },
    async loadCompletedMeetings() {
      try {
        this.isLoadingMeetings = true;
        const params = {
          ...this.scheduleQueryBase(true),
          page: 1,
          per_page: SCHEDULE_PREFETCH_LIMIT,
        };
        const { data } = await axios.get(`${this.accountScopedBase}/today_meetings`, { params });
        this.applyMeetingDemoStatus(data);
        const upcoming = data?.upcoming?.data || [];
        const completed = data?.past?.data || [];
        const total = data?.past?.total_count || 0;
        if (!total) {
          this.showAlert('No completed meetings found');
          return;
        }
        const toMeetingStartMs = item => {
          const direct = item?.meeting_starts_at;
          const ms = direct ? new Date(direct).getTime() : NaN;
          if (!Number.isNaN(ms)) return ms;
          const fallback = item?.additional_attributes?.demo_timing;
          const fallbackMs = fallback ? new Date(String(fallback).replace(/,/g, '')).getTime() : NaN;
          return Number.isNaN(fallbackMs) ? Number.POSITIVE_INFINITY : fallbackMs;
        };
        const completedSorted = [...completed].sort(
          (a, b) => toMeetingStartMs(a) - toMeetingStartMs(b)
        );
        const upcomingSorted = [...upcoming].sort(
          (a, b) => toMeetingStartMs(a) - toMeetingStartMs(b)
        );
        this.meetingsPageIndex = 1;
        this.meetingItems = [...completedSorted, ...upcomingSorted];
        // We pre-fetched everything (per_page=200) and paginate client-side via
        // displayMeetingItems. count is the actually-loaded size so pagination is exact
        // (no empty trailing pages). perPage stays at a normal value so ve-pagination
        // renders (perPage=200 collapses to a single page and hides the control).
        // upcomingTotal is preserved so the tab badge keeps showing upcoming + completed.
        this.meetingsMeta = {
          count: this.meetingItems.length,
          upcomingTotal: data?.upcoming?.total_count || 0,
          currentPage: 1,
          perPage: SCHEDULE_PAGE_SIZE,
          completedTodayCount: total,
        };
        this.showCompletedMeetings = true;
      } catch (e) {
        this.showAlert('No completed meetings found');
      } finally {
        this.isLoadingMeetings = false;
      }
    },
    hideCompletedMeetings() {
      this.meetingsPageIndex = 1;
      this.fetchMeetings();
    },
    async loadCompletedReminders() {
      try {
        this.isLoadingReminders = true;
        const params = {
          ...this.scheduleQueryBase(true),
          page: 1,
          per_page: SCHEDULE_PREFETCH_LIMIT,
        };
        const { data } = await axios.get(`${this.accountScopedBase}/today_reminders`, { params });
        const upcoming = data?.upcoming?.data || [];
        const completed = data?.past?.data || [];
        const total = data?.past?.total_count || 0;
        if (!total) {
          this.showAlert(
            this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.NO_COMPLETED_REMINDERS_ALERT')
          );
          return;
        }
        const toReminderStartMs = item => {
          const direct = item?.meeting_starts_at || item?.additional_attributes?.calendar_timings;
          const ms = direct ? new Date(direct).getTime() : NaN;
          return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
        };
        const completedSorted = [...completed].sort(
          (a, b) => toReminderStartMs(a) - toReminderStartMs(b)
        );
        const upcomingSorted = [...upcoming].sort(
          (a, b) => toReminderStartMs(a) - toReminderStartMs(b)
        );
        this.remindersPageIndex = 1;
        this.items = [...completedSorted, ...upcomingSorted];
        // We pre-fetched everything (per_page=200) and paginate client-side via
        // displayReminderItems. count is the actually-loaded size so pagination is exact
        // (no empty trailing pages). perPage stays at a normal value so ve-pagination
        // renders (perPage=200 collapses to a single page and hides the control).
        // upcomingTotal is preserved so the tab badge keeps showing upcoming + completed.
        this.remindersMeta = {
          count: this.items.length,
          upcomingTotal: data?.upcoming?.total_count || 0,
          currentPage: 1,
          perPage: SCHEDULE_PAGE_SIZE,
          completedTodayCount: total,
        };
        this.showCompletedReminders = true;
      } catch (e) {
        this.showAlert(
          this.$t('OVERVIEW_REPORTS.PRIORITY_LEADS.NO_COMPLETED_REMINDERS_ALERT')
        );
      } finally {
        this.isLoadingReminders = false;
      }
    },
    hideCompletedReminders() {
      this.remindersPageIndex = 1;
      this.fetch();
    },
    openScheduleContactPanel(contactId) {
      const resolvedId = String(contactId).includes('-m-')
        ? String(contactId).split('-m-')[0]
        : contactId;
      const matchId = item => String(item?.id) === String(resolvedId);
      const contact =
        this.items.find(matchId) || this.meetingItems.find(matchId);
      if (contact) {
        this.Contact = contact;
        this.selectedContactId = contact.id;
        this.showContactInfoPanelPane = true;
      }
    },
  },
};
</script>

<style scoped>
/* Reserve space for the vertical scrollbar so toggling scroll (e.g. tab switch) does not shift layout horizontally. */
.live-reports-root {
  scrollbar-gutter: stable;
}

.priority-leads-card {
  --overview-leads-body-height: calc(602px + 1rem + 1.21875rem - 2px);
}

.priority-leads-card :deep(.card) {
  overflow: visible;
  /* Entire card stacks below sidebar (z-33); keeps filter popovers from competing with nav */
  position: relative;
  z-index: 10;
  isolation: isolate;
}

.priority-leads-card .table-wrapper :deep(.contact-table-container) {
  height: var(--overview-leads-body-height);
  flex: 0 0 auto;
  min-height: 0;
}

.priority-leads-card .table-wrapper :deep(.contact-table-container > div:first-child) {
  flex: 1 1 auto;
  height: 0 !important;
  min-height: 0;
  overflow: hidden;
}

.priority-leads-card .table-wrapper :deep(.contact-table-container > div:last-child) {
  flex-shrink: 0;
}

.priority-leads-card :deep(.card-header) {
  overflow: visible;
  /* Header (filters + dropdowns) must paint above .card-body — body follows in DOM and would cover overflow otherwise */
  position: relative;
  z-index: 22;
}

.priority-leads-card :deep(.card-body) {
  position: relative;
  z-index: 1;
}

.schedule-panel {
  flex-direction: column;
}

.priority-leads-card :deep(.card-header--title-area),
.priority-leads-card :deep(.card-header--control-area) {
  min-width: 0;
}

.priority-leads-card :deep(.card-header--title-area h5:empty) {
  display: none;
}

.priority-leads-toolbar {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
}

.priority-leads-filter-slot {
  /* Was 8.25rem — too tight for "Select a segment"; keep cap so long segment names still ellipsis */
  max-width: 10.75rem;
  min-width: 0;
  flex-shrink: 1;
}

.priority-leads-filter-slot--dropdown {
  position: relative;
  z-index: auto;
}

/* Slightly raise Agent/Segment slots so overlapping menus stack predictably; still < sidebar */
.priority-leads-filter-slot--dock-end {
  z-index: 2;
}

.priority-leads-dropdown-anchor {
  position: absolute;
  top: calc(100% + 6px);
  width: 0;
  height: 0;
  z-index: 24;
}

.priority-leads-dropdown-anchor--dock-start {
  inset-inline-start: 0;
}

.priority-leads-dropdown-anchor--dock-end {
  inset-inline-end: 0;
}

.priority-leads-card :deep(.priority-leads-filter-btn.button) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.priority-leads-card :deep(.priority-leads-filter-btn .button__content) {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
}

.priority-leads-filter-text {
  font-size: 0.8125rem;
  line-height: 1.35;
}

.modal-overlay-v1 {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 39;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
}

/* Schedule tabs styling */
.schedule-tabs {
  margin-bottom: 0;
  border-bottom: none;
}

.schedule-tabs :deep(.tabs) {
  margin-bottom: 0;
  border-bottom: none;
  padding-left: 0 !important;
}

/* Date range select styling - using standard multiselect-wrap--small */
.multiselect-wrap--small {
  width: 200px;
}

/* Reminders section wrapper - fixed height and min-width to prevent shrinking on tab switch */
.reminders-section-wrapper {
  min-height: 590px;
  min-width: 620px;
  overflow: visible;
}

.admin-call-stack {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
}

.admin-call-stack__section {
  width: 100%;
  min-width: 0;
}

.admin-call-stack__section :deep(.card-body) {
  overflow-x: auto;
  min-width: 0;
}

/* Pin the wrap-point of the leads/schedule row to 1387px. */
@media screen and (max-width: 1387px) {
  .leads-schedule-row {
    flex-direction: column;
  }
}

/* Schedule content wrapper - ensures vertical stacking and consistent width */
.schedule-content-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 620px;
  gap: 0;
}

/* Custom date picker styling */
.custom-date-picker-wrapper {
  padding-bottom: var(--space-small);
  min-height: 100px;
  width: 100%;
  display: block;
}

.custom-date-picker-row {
  display: inline-flex;
  align-items: flex-end;
  gap: var(--space-normal);
  padding: var(--space-small) var(--space-normal);
  background: var(--s-25);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--s-100);
}

.dark .custom-date-picker-row {
  background: var(--s-800);
  border-color: var(--s-600);
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--s-600);
  text-transform: capitalize;
}

.dark .date-label {
  color: var(--s-300);
}

.date-input {
  padding: 8px 12px;
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  font-size: 13px;
  background: var(--white);
  min-width: 130px;
  height: 36px;
  color: var(--s-700);
}

.dark .date-input {
  background: var(--s-900);
  color: var(--s-100);
  border-color: var(--s-600);
}

.date-input:focus {
  outline: none;
  border-color: var(--w-500);
  box-shadow: 0 0 0 2px var(--w-75);
}

.dark .date-input:focus {
  box-shadow: 0 0 0 2px var(--w-800);
}

.apply-btn {
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--border-radius-normal);
  white-space: nowrap;
  align-self: center;
  margin-top: 0.75rem;
}
.board-button__text {
  line-height: 1.3;
}

/* Icon-only segment open: center fullscreen glyph in the gray square (fixes top-left misalignment). */
.priority-leads-segment-open-btn.button.button--only-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
}

.priority-leads-segment-open-btn :deep(.icon) {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.priority-leads-segment-open-btn :deep(svg) {
  display: block;
}
.schedule-header-row {
  display: flex;
  align-items: center;
  gap: var(--space-small);
}

.reload-schedule-btn {
  min-width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.priority-leads-reload-btn {
  min-width: 28px;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}
/* Blur loading effect */
.table-wrapper {
  position: relative;
}

/* Priority Leads only: keep scroll behavior but hide the vertical scrollbar track */
.priority-leads-card .table-wrapper :deep(.ve-table-container) {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.priority-leads-card .table-wrapper :deep(.ve-table-container::-webkit-scrollbar) {
  width: 0;
  height: 0;
  display: none;
}

.blurred-content {
  filter: blur(2px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  transition: all 0.2s ease;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(2px);
  z-index: 5;
}

.dark .loading-overlay {
  background: rgba(0, 0, 0, 0.2);
}

::v-deep .spinner.small {
  background: none !important;
}
</style>
